import { Component, inject, OnInit, ViewChild, TemplateRef, AfterViewInit, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageShell } from '../../shared/components/page-shell/page-shell';
import { ChartModule } from 'primeng/chart';
import { AppPrimeTable, PrimeTableColumn } from '../../shared/components/prime-table/prime-table';
import { MobileCardList } from '../../shared/components/mobile-card-list/mobile-card-list.component';
import { AppDropdown } from '../../shared/components/dropdown/dropdown';
import { SignedSrcDirective } from '../../shared/directives/signed-src.directive';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { selectPermissions, selectUserInfo, selectUserStatus, selectBranchId } from '../../core/store/auth/auth.selectors';
import { combineLatest, Observable, BehaviorSubject, Subscription, of } from 'rxjs';
import { map, take, filter, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { DashboardService } from '../../core/services/dashboard.service';
import { formatNumber } from '../../core/utils/number.utils';
import { appointmentStatusKey } from '../../core/utils/appointment-status.utils';

import { NoPermissionComponent } from '../../shared/components/no-permission/no-permission.component';
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { financialStatusKey } from '../../core/utils/financial-status.utils';

// Register the plugin globally (or could be done per chart if using plugins array with some setup)
Chart.register(ChartDataLabels);

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, PageShell, TranslateModule, ChartModule, AppPrimeTable, MobileCardList, NoPermissionComponent, AppDropdown, SignedSrcDirective],
  templateUrl: './dashboard.html',
})
export class Dashboard implements OnInit, AfterViewInit {
  readonly getFinancialStatusKey = financialStatusKey;
  private store = inject(Store);
  private dashboardService = inject(DashboardService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  
  permissions$ = this.store.select(selectPermissions);
  userStatus$ = this.store.select(selectUserStatus);
  userInfo$ = this.store.select(selectUserInfo);

  canAccessDashboard$: Observable<boolean> = combineLatest([this.permissions$, this.userStatus$]).pipe(
    map(([permissions, status]) => {
      if (status === 'all') return true;
      return permissions ? permissions.includes('Dashboard') : false;
    })
  );

  // Stats data
  appointmentStats$ = new BehaviorSubject<any[]>([]);
  // Only statuses that actually appear in the chart (non-zero count), so the
  // legend matches the pie slices instead of listing every possible status.
  activeAppointmentStats$ = this.appointmentStats$.pipe(
    map(stats => (stats || []).filter(s => (s.total_count || 0) > 0)),
  );
  paymentsSummary$ = new BehaviorSubject<any>(null);
  weeklyTransactions$ = new BehaviorSubject<any[]>([]);
  expenseRecords$ = new BehaviorSubject<any[]>([]);
  onlineUsers$ = new BehaviorSubject<number>(0);
  totalAppointments$ = new BehaviorSubject<number>(0);
  maxChartValue$ = new BehaviorSubject<number>(1000);

  chartData: any;
  chartOptions: any;
  
  selectedPeriod = 'Diário';
  isLoadingStats = false;
  
  // Date range for checking account chart
  chartStartDate = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  chartEndDate = new Date().toISOString().split('T')[0];
  
  showDatePicker = false;
  activePreset = 'period_last_7_days';
  
  @ViewChild('patientTemplate') patientTemplate!: TemplateRef<any>;
  @ViewChild('appointmentStatusTemplate') appointmentStatusTemplate!: TemplateRef<any>;
  @ViewChild('paymentStatusTemplate') paymentStatusTemplate!: TemplateRef<any>;
  @ViewChild('valueTemplate') valueTemplate!: TemplateRef<any>;

  summaryColumns: any[] = [
    { field: 'date_created', header: 'table_date' },
    { field: 'value', header: 'table_value', isValue: true },
    { field: 'patient', header: 'table_patient' },
    { field: 'appointmentStatus', header: 'table_appointment_status' },
    { field: 'paymentStatus', header: 'table_payment_status' }
  ];

  summaryTemplates: { [key: string]: TemplateRef<any> } = {};
  


  ngOnInit() {
    this.initChartOptions();

    this.appointmentStats$.subscribe(stats => {
      this.updateDoughnutChart(stats);
    });

    // Chart labels are translated in TS (the built-in legend is off), so rebuild
    // them when the user switches language — the HTML legends update on their own
    // via the translate pipe, but the chart's tooltip labels do not.
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateDoughnutChart(this.appointmentStats$.value);
    });

    this.store.select(selectBranchId).pipe(
      filter(id => !!id),
      distinctUntilChanged(),
      // Gate: Only proceed if user has permission
      switchMap(branchId => this.canAccessDashboard$.pipe(
        take(1),
        filter(canAccess => canAccess), // Stop here if false
        map(() => branchId)
      ))
    ).subscribe(branchId => {
      this.loadAllData(branchId!);
    });

    this.store.select(selectBranchId).pipe(
      filter(id => !!id),
      distinctUntilChanged(),
      // Gate: Only proceed if user has permission
      switchMap(branchId => this.canAccessDashboard$.pipe(
        take(1),
        filter(canAccess => canAccess), // Stop here if false
        switchMap(() => this.dashboardService.getOnlineUsers(branchId!))
      ))
    ).subscribe(count => {
      this.onlineUsers$.next(count);
    });
  }

  initChartOptions() {
    this.chartOptions = {
      cutout: '40%',
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
            callbacks: {
                label: function(context: any) {
                    let label = context.label || '';
                    if (label) {
                        label += ': ';
                    }
                    if (context.parsed !== null) {
                         const total = context.chart._metasets[context.datasetIndex].total;
                         const percentage = Math.round((context.parsed / total) * 100);
                         label += context.parsed + ' (' + percentage + '%)';
                    }
                    return label;
                }
            }
        },
        datalabels: {
            color: '#ffffff',
            font: {
                weight: 'bold',
                size: 10
            },
            formatter: (value: any, ctx: any) => {
                if (ctx.chart.data.datasets.length > 0) {
                     const total = ctx.chart._metasets[ctx.datasetIndex].total;
                     const percentage = Math.round((value / total) * 100);
                     return percentage > 0 ? percentage + '%' : '';
                }
                return '';
            }
        }
      }
    };
  }

  updateDoughnutChart(stats: any[]) {
    if (!stats || stats.length === 0) {
      this.chartData = null;
      return;
    }

    // Show only statuses with appointments; the legend uses the same filter
    // (activeAppointmentStats$) so slices and legend entries always match.
    const activeStats = stats.filter(s => (s.total_count || 0) > 0);

    if (activeStats.length === 0) {
      this.chartData = null;
      return;
    }

    this.chartData = {
      labels: activeStats.map(s => this.translate.instant(appointmentStatusKey(s.status_name))),
      datasets: [
        {
          data: activeStats.map(s => s.total_count),
          backgroundColor: activeStats.map(s => s.color_bg),
          hoverBackgroundColor: activeStats.map(s => s.color_bg),
          borderWidth: 0
        }
      ]
    };
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.summaryTemplates = {
        patient: this.patientTemplate,
        appointmentStatus: this.appointmentStatusTemplate,
        paymentStatus: this.paymentStatusTemplate,
        value: this.valueTemplate
      };
    });
  }



  async loadAllData(branchId: number) {
    this.isLoadingStats = true;
    try {
      const [appts, payments, expenses] = await Promise.all([
        this.dashboardService.getAppointmentStats(branchId, this.selectedPeriod),
        this.dashboardService.getPaymentsSummary(branchId),
        this.dashboardService.getExpenseRecords(branchId)
      ]);

      if (appts.data) {
        this.appointmentStats$.next(appts.data);
        const total = appts.data.reduce((acc: number, item: any) => acc + (item.total_appointments || 0), 0);
        this.totalAppointments$.next(total);
      }
      if (payments.data) this.paymentsSummary$.next(payments.data[0]);
      if (expenses.data) this.expenseRecords$.next(expenses.data.slice(0, 8));

      // Load transactions for line chart using current range
      const weekly = await this.dashboardService.getWeeklyTransactionSummary(branchId, this.chartStartDate, this.chartEndDate);
      if (weekly.data) {
        this.updateChartData(weekly.data);
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      this.isLoadingStats = false;
    }
  }

  async onPeriodChange(newPeriod: string) {
    this.selectedPeriod = newPeriod;
    const branchId = await this.store.select(selectBranchId).pipe(take(1)).toPromise();
    if (branchId) {
      const appts = await this.dashboardService.getAppointmentStats(branchId, newPeriod);
      if (appts.data) {
        this.appointmentStats$.next(appts.data);
        const total = appts.data.reduce((acc: number, item: any) => acc + (item.total_appointments || 0), 0);
        this.totalAppointments$.next(total);
      }
    }
  }

  hasPermission(permission: string): Observable<boolean> {
    return combineLatest([this.permissions$, this.userStatus$] as [Observable<string[] | null>, Observable<string | null>]).pipe(
      map(([perms, status]) => {
        if (status === 'all') return true;
        if (!perms) return false;
        return perms.includes(permission);
      })
    );
  }

  async onDateRangeChange(start: string, end: string) {
    this.chartStartDate = start;
    this.chartEndDate = end;
    const branchId = await this.store.select(selectBranchId).pipe(take(1)).toPromise();
    if (branchId) {
      const weekly = await this.dashboardService.getWeeklyTransactionSummary(branchId, start, end);
      if (weekly.data) {
        this.updateChartData(weekly.data);
      }
    }
  }

  updateChartData(data: any[]) {
    this.weeklyTransactions$.next(data);
    const max = data.reduce((acc, item) => Math.max(acc, item.total_entrada || 0), 0);
    
    // Nice rounded max for Y-axis (e.g., 500, 1000, 2000, 5000, 10000)
    let niceMax = 1000;
    if (max > 0) {
      if (max <= 500) niceMax = 500;
      else if (max <= 1000) niceMax = 1000;
      else if (max <= 2000) niceMax = 2000;
      else if (max <= 5000) niceMax = 5000;
      else niceMax = Math.ceil(max / 1000) * 1000;
    }
    
    this.maxChartValue$.next(niceMax);
  }

  getXAxisInterval(count: number): number {
    if (count <= 10) return 1;
    if (count <= 20) return 2;
    if (count <= 60) return 7; // Weekly labels for 1-2 months
    if (count <= 180) return 30; // Monthly labels for 3-6 months
    return Math.ceil(count / 6); // Default to ~6 labels
  }

  get yAxisLabels(): number[] {
    const max = this.maxChartValue$.value;
    return [max, max / 2, 0];
  }

  getPaymentStatusClass(status: string): string {
    const s = status?.trim() || '';
    if (s === 'Pago' || s === 'Honorário Pago') {
      return 'status-success';
    }
    if (['À Faturar', 'Lançar Custos', 'Aguardando Faturamento', 'Aguardando Pagamento'].includes(s)) {
      return 'status-warning';
    }
    if (s === 'Faturado') {
      return 'status-faturado';
    }
    // Default
    return 'status-danger';
  }

  trackBySegment(index: number, item: any): any {
    return item.status_name || index;
  }

  toggleDatePicker() {
    this.showDatePicker = !this.showDatePicker;
  }

  setPresetRange(preset: string) {
    this.activePreset = preset;
    const today = new Date();
    let start: Date;
    let end: Date = today;

    switch (preset) {
      case 'period_today':
        start = today;
        break;
      case 'period_last_7_days':
        start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
        break;
      case 'period_last_30_days':
        start = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000);
        break;
      case 'period_this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'period_last_month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        return;
    }

    this.chartStartDate = start.toISOString().split('T')[0];
    this.chartEndDate = end.toISOString().split('T')[0];
    this.onDateRangeChange(this.chartStartDate, this.chartEndDate);
    this.showDatePicker = false;
  }

  formatMoney(value: any): string {
    return formatNumber(Number(value) || 0);
  }

  /**
   * i18n key (or raw name) for an appointment status, for use with `| translate`
   * in the template. Backed by the shared status map so the chart, its legends,
   * and any future consumer stay in sync.
   */
  getAppointmentStatusKey(statusName: string | null | undefined): string {
    return appointmentStatusKey(statusName);
  }

  getPeriodTranslationKey(period: string): string {
    const map: { [key: string]: string } = {
      'Diário': 'period_daily',
      'Semanal': 'period_weekly',
      'Mensal': 'period_monthly',
      'Trimestral': 'period_quarterly'
    };
    return map[period] || 'period_daily';
  }
}
